import { Close as CloseIcon } from '@mui/icons-material';
import { DialogTitle, SxProps, Theme } from '@mui/material';
import { DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import React, { FC, PropsWithChildren, ReactNode } from 'react';

import { DialogRoot, CloseButton } from './Dialog.styles';

export interface BhiDialogProps extends Omit<MuiDialogProps, 'open' | 'onClose' | 'title'> {
  title?: ReactNode;
  isOpen?: boolean;
  open?: boolean;
  onClose?: () => void;
  paperSx?: SxProps<Theme>;
  showCloseButton?: boolean;
  closeButtonDisabled?: boolean;
}

export const BhiDialog: FC<PropsWithChildren<BhiDialogProps>> = (props) => {
  const {
    title,
    isOpen,
    open: openProp,
    onClose,
    paperSx,
    showCloseButton,
    closeButtonDisabled,
    disablePortal = false,
    children,
    PaperProps,
    ...dialogProps
  } = props;

  const isDialogOpen = isOpen ?? openProp ?? false;
  const shouldRenderHeader = title !== undefined && title !== null;
  const shouldShowCloseButton = showCloseButton ?? (shouldRenderHeader && Boolean(onClose));

  const handleDialogClose: MuiDialogProps['onClose'] | undefined = onClose
    ? () => onClose()
    : undefined;

  const paperPropsSx = PaperProps?.sx;
  const mergedPaperSx = [
    ...(Array.isArray(paperPropsSx) ? paperPropsSx : [paperPropsSx]),
    ...(Array.isArray(paperSx) ? paperSx : [paperSx]),
  ].filter(Boolean) as SxProps<Theme>;

  return (
    <DialogRoot
      $showCloseButton={shouldShowCloseButton}
      open={isDialogOpen}
      onClose={handleDialogClose}
      PaperProps={{ ...PaperProps, sx: mergedPaperSx }}
      disablePortal={disablePortal}
      disableScrollLock={disablePortal}
      disableRestoreFocus={disablePortal}
      disableAutoFocus={disablePortal}
      disableEnforceFocus={disablePortal}
      keepMounted={false}
      {...dialogProps}
    >
      {isDialogOpen && (
        <>
          {shouldRenderHeader && <DialogTitle variant="h6">{title}</DialogTitle>}

          {shouldShowCloseButton && (
            <CloseButton
              aria-label="Close"
              onClick={onClose}
              disabled={closeButtonDisabled}
            >
              <CloseIcon />
            </CloseButton>
          )}

          {children}
        </>
      )}
    </DialogRoot>
  );
};

export { BhiDialog as Dialog };
export type { BhiDialogProps as DialogProps };
export { CloseButton as DialogCloseButton } from './Dialog.styles';
